import { useTranslate } from '_core/hooks/useTranslate';
import Button from '_core/modules/atoms/Button';
import CloseMiniButton from '_core/modules/atoms/CloseMiniButton';
import * as auth from '_core/store/auth.duck';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

function SyncProgress({ syncStatus, isExiting, onLocalExit }) {
  const { t } = useTranslate();
  const dispatch = useDispatch();

  const [percentage, setPercentage] = useState(0);
  const [showCloseButton, setShowCloseButton] = useState(false);

  useEffect(() => {
    setPercentage(getProgressPercentage());

    return () => {
      setPercentage(0);
    };
  }, [syncStatus.progress]);

  useEffect(() => {
    if (syncStatus.overallStatus === 'completed' || syncStatus.overallStatus === 'error' || syncStatus.overallStatus === 'failed') {
      setShowCloseButton(true);
    }
  }, [syncStatus.overallStatus]);

  function handleRefreshPage() {
    // Siempre limpiar el syncJob para ocultar el componente
    dispatch(auth.actions.clearSyncJob());
    window.location.reload();
  }

  function handleClose() {
    // Notificar al contenedor que inicie la animación de salida
    if (onLocalExit) {
      onLocalExit();
    }

    // Limpiar el syncJob después de la animación (300ms)
    setTimeout(() => {
      dispatch(auth.actions.clearSyncJob());
    }, 300);
  }

  const getProgressPercentage = () => {
    if (!syncStatus.progress || Object.keys(syncStatus.progress).length === 0) {
      return 0;
    }

    const totalItems = Object.keys(syncStatus.progress).length;
    const completedItems = Object.values(syncStatus.progress).filter((item) => item.status === 'completed').length;

    return Math.round((completedItems / totalItems) * 100);
  };

  const getStatusText = () => {
    if (syncStatus.overallStatus === 'completed') {
      return { title: t('sync:Synchronization completed'), body: t('sync:Sync is completed but you need to refresh the page'), button: true };
    }
    if (syncStatus.overallStatus === 'error' || syncStatus.overallStatus === 'failed') {
      return { title: t('sync:Synchronization failed'), body: t('sync:We are sorry, but we couldn’t complete the synchronization'), button: false };
    }
    return {
      title: t('sync:Sync in progress'),
      body: t('sync:We are syncing your data...'),
      footer: t('sync:It will take just a moment, thank you for your patience!'),
      button: false,
    };
  };

  return (
    <div className={clsx('sync-progress', { 'sync-progress--is-exiting': isExiting })}>
      <div className="sync-progress__container">
        {showCloseButton && (
          <div className="sync-progress__close-button">
            <CloseMiniButton onClick={handleClose} />
          </div>
        )}
        <div className="sync-progress__header">
          <h3 className="sync-progress__title">
            <span className="sync-progress__icon">🛠️</span> {getStatusText().title}
          </h3>
          <div className="sync-progress__percentage">{`${percentage}%`}</div>
        </div>
        <div className="sync-progress__body">
          <p className="sync-progress__body-text">{getStatusText().body}</p>
          <p className="sync-progress__body-text">{getStatusText()?.footer}</p>
        </div>
        <div className="sync-progress__footer">
          {getStatusText().button && (
            <Button className="sync-progress__button" onClick={handleRefreshPage} size="small" text={t('sync:Refresh page')} variant="inline" />
          )}
        </div>
      </div>
    </div>
  );
}

SyncProgress.propTypes = {
  syncStatus: PropTypes.shape({
    isLoading: PropTypes.bool,
    isCompleted: PropTypes.bool,
    error: PropTypes.string,
    progress: PropTypes.object,
    overallStatus: PropTypes.string,
  }).isRequired,
  isExiting: PropTypes.bool,
  onLocalExit: PropTypes.func,
};

SyncProgress.defaultProps = {
  isExiting: false,
  onLocalExit: null,
};

export default SyncProgress;
